import { BaseMapper, type IPaginationMapper } from './BaseMapper';
import type { components } from '@/core/types/generated/api';
import { type Project } from '@/core/types/models/Project';

// Import DTO types
export type ProjectDto = components['schemas']['ProjectDto'];
export type ProjectTeamMemberDto = components['schemas']['ProjectTeamMemberDto'];
export type CreateProjectCommand = components['schemas']['CreateProjectCommand'];
export type UpdateProjectCommand = components['schemas']['UpdateProjectCommand'];
export type ApiProjectStatus = components['schemas']['ProjectStatus'];
export type ApiProjectRole = components['schemas']['ProjectRole'];

// UI Models (extending the existing models)
export interface UiProject extends Project {
  lastModifiedAt?: Date | null;
  // Strong label for status for UI display
  statusLabel?: string; // derived from status via pascalToHuman
}

export interface UiProjectMember {
  userId: string;
  role: ApiProjectRole; // Store the API enum value strongly-typed
  roleLabel?: string; // Human label for UI
  fullName: string;
  email?: string | null;
}

// Data transfer interfaces for create/update operations
export interface CreateProjectData {
  name: string;
  description?: string;
  icon?: string;
  status?: ApiProjectStatus; // API string enum
  statusLabel?: string; // Optional UI label that can be mapped back
  members?: Array<{
    userId: string;
    role?: ApiProjectRole;
    roleLabel?: string;
  }>;
  isTemplate?: boolean;
  templateId?: string;
  connectionString?: string;
}

export interface UpdateProjectData {
  id: string;
  name: string;
  description?: string;
  connectionString?: string;
  status?: ApiProjectStatus;
  statusLabel?: string;
  isTemplate?: boolean;
  templateId?: string;
}

/**
 * ProjectMapper - Maps between ProjectDto and UiProject
 */
export class ProjectMapper extends BaseMapper<ProjectDto, UiProject> implements IPaginationMapper {
  
  mapToUiModel(dto: ProjectDto): UiProject {
    const rawStatus = (dto.status as ApiProjectStatus | undefined) ?? (dto.isActive ? 'Active' : 'Archived');
    const status = this.apiStatusToProjectStatus(rawStatus);
    return {
      id: dto.id || '',
      name: dto.name || '',
      description: this.mapString(dto.description),
      status,
      statusLabel: this.toEnumLabel(rawStatus),
      createdAt: this.mapDate(dto.createdAt) || new Date(),
      updatedAt: this.mapDate(dto.lastModifiedAt) || new Date(),
      lastModifiedAt: this.mapDate(dto.lastModifiedAt),
      createdBy: dto.createdBy || null,
      lastModifiedBy: dto.lastModifiedBy || null,
      members: this.mapArray(dto.teamMembers).map(member => this.mapProjectMember(member)),
      icon: dto.icon || 'fa-solid fa-folder'
    };
  }

  mapToDto(uiModel: Partial<UiProject>): Partial<ProjectDto> {
    const status = (uiModel.status as ApiProjectStatus | undefined) ??
      this.labelToEnum<ApiProjectStatus>(uiModel.statusLabel, [
        'Pending','Active','Archived','Template'
      ] as const);
    return {
      name: uiModel.name,
      description: uiModel.description,
      icon: uiModel.icon,
      status,
    };
  }

  mapPaginationInfo(paginationData: any): {
    totalItems: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
    hasNext: boolean;
    hasPrevious: boolean;
  } {
    return {
      totalItems: paginationData?.totalItems || 0,
      totalPages: paginationData?.totalPages || 1,
      currentPage: paginationData?.currentPage || 1,
      pageSize: paginationData?.pageSize || 10,
      hasNext: paginationData?.hasNext || false,
      hasPrevious: paginationData?.hasPrevious || false
    };
  }

  /**
   * Map project member DTO to UI model
   */
  private mapProjectMember(memberDto: ProjectTeamMemberDto): UiProjectMember {
    return {
      userId: memberDto.userId || '',
      role: (memberDto.role || 'ProjectViewer'),
      roleLabel: this.toEnumLabel(memberDto.role || undefined),
      fullName: memberDto.fullName || '',
      email: memberDto.email ?? null,
    };
  }

  /**
   * Map CreateProjectData to CreateProjectCommand
   */
  mapCreateDataToCommand(data: CreateProjectData): CreateProjectCommand {
    const roleFromLabel = (label?: string): ApiProjectRole | undefined =>
      this.labelToEnum<ApiProjectRole>(label ?? null, [
        'ProjectOwner','ProjectContributor','ProjectViewer','ProjectValidator'
      ] as const);
    return {
      name: data.name,
      description: data.description || null,
      icon: data.icon || null,
      isTemplate: data.isTemplate || false,
      templateId: data.templateId || null,
      teamMembers: data.members?.map(member => ({
        userId: member.userId,
        role: member.role ?? roleFromLabel(member.roleLabel) ?? 'ProjectViewer'
      })) || null
    };
  }

  /**
   * Map UpdateProjectData to UpdateProjectCommand
   */
  mapUpdateDataToCommand(data: UpdateProjectData): UpdateProjectCommand {
    const status = data.status ?? this.labelToEnum<ApiProjectStatus>(data.statusLabel, [
      'Pending','Active','Archived','Template'
    ] as const);
    return {
      id: data.id,
      name: data.name || null,
      description: data.description || null,
      status: status,
      templateId: data.templateId || null
    };
  }

  /**
   * Normalize API project status to the Project model's status (Project['status'])
   */
  private apiStatusToProjectStatus(status: ApiProjectStatus): Project['status'] {
    switch (status) {
      case 'PendingDeletion':
        // Map PendingDeletion to Archived for UI/model compatibility
        return 'Archived';
      case 'Pending':
      case 'Active':
      case 'Archived':
      case 'Template':
        return status as Project['status'];
      default:
        return 'Archived';
    }
  }

  // Public helpers for external usage/back-compat
  public mapStatusToApi(statusOrLabel?: string): ApiProjectStatus | undefined {
    return this.labelToEnum<ApiProjectStatus>(statusOrLabel, [
      'Pending','Active','Archived','Template'
    ] as const);
  }

  public mapRoleToApi(roleOrLabel?: string): ApiProjectRole | undefined {
    return this.labelToEnum<ApiProjectRole>(roleOrLabel, [
      'ProjectOwner','ProjectContributor','ProjectViewer','ProjectValidator'
    ] as const);
  }
}

// Export singleton instance
export const projectMapper = new ProjectMapper();
